import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, Globe, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";

export default function ProgramDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/programs/${id}`).then((r) => setProgram(r.data)).catch(() => navigate("/programs"));
  }, [id, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!program) return;
    setBusy(true);
    try {
      if (program.price > 0) {
        const { data } = await api.post("/checkout/session", {
          program_id: program.id,
          origin_url: window.location.origin,
          name: form.name,
          email: form.email,
        });
        window.location.href = data.url;
      } else {
        await api.post("/registrations", { program_id: program.id, ...form });
        toast.success("Registered. Anant's team will reach out with next steps.");
        setForm({ name: "", email: "", phone: "", message: "" });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  if (!program) {
    return <div className="max-w-7xl mx-auto px-6 py-24 text-ub-2">Loading…</div>;
  }

  return (
    <div data-testid="program-detail-page" className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <Link to="/programs" className="inline-flex items-center gap-2 text-ub-2 hover:text-primary-ub mb-8">
        <ArrowLeft size={16} /> All programs
      </Link>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <span className="text-xs uppercase tracking-widest text-primary-ub">{program.category}</span>
          <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tighter mt-3">{program.title}</h1>
          <p className="mt-4 text-xl text-ub-2 italic">{program.tagline}</p>
          {program.quote && (
            <p className="mt-6 font-script text-3xl text-primary-ub leading-snug max-w-md">"{program.quote}"</p>
          )}
          <p className="mt-6 text-lg text-ub-2 leading-relaxed">{program.description}</p>
          <div className="flex flex-wrap items-center gap-6 mt-8 text-ub-2">
            <span className="inline-flex items-center gap-2"><Clock size={16} className="text-primary-ub" /> {program.duration}</span>
            <span className="inline-flex items-center gap-2"><Globe size={16} className="text-primary-ub" /> {program.format}</span>
            <span className="inline-flex items-center gap-2 font-medium text-ub">
              {program.price > 0 ? formatPrice(program.price, program.currency) : "Free / By donation"}
            </span>
          </div>
          <div className="mt-12">
            <h2 className="font-serif text-3xl mb-4">What you'll experience</h2>
            <ul className="space-y-3">
              {program.highlights?.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-ub-2">
                  <Check size={18} className="text-primary-ub mt-1 shrink-0" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
          {program.includes && (
            <div className="mt-12">
              <h2 className="font-serif text-3xl mb-4">Program includes</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {program.includes.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 text-ub-2">
                    <Check size={18} className="text-primary-ub mt-1 shrink-0" />
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {program.for_whom && (
            <div className="mt-12">
              <h2 className="font-serif text-3xl mb-4">Who should join?</h2>
              <div className="flex flex-wrap gap-2">
                {program.for_whom.map((f) => (
                  <span key={f} className="text-sm border border-ub rounded-full px-4 py-1.5 text-ub-2 bg-ub-alt">{f}</span>
                ))}
              </div>
            </div>
          )}
          {program.what_you_need && (
            <div className="mt-12">
              <h2 className="font-serif text-3xl mb-4">What you need for each session</h2>
              <div className="flex flex-wrap gap-2">
                {program.what_you_need.map((w) => (
                  <span key={w} className="text-sm border border-ub rounded-full px-4 py-1.5 text-ub-2">{w}</span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-12 bg-ub-alt border border-ub rounded-3xl p-6">
            <div className="text-sm uppercase tracking-widest text-primary-ub">Facilitated by</div>
            <div className="font-serif text-3xl mt-1">Anant Pandit</div>
            <p className="text-ub-2 mt-2 text-sm">Internationally Certified Heal Your Life® Workshop Leader · HYL Teens Teacher · Life Coach · Leading Business with Heart & Mind Facilitator</p>
          </div>
        </div>
        <div className="md:col-span-5">
          <form onSubmit={submit} className="bg-ub-alt border border-ub rounded-[2rem] p-8 sticky top-24" data-testid="program-register-form">
            <div className="font-serif text-3xl">Reserve your seat</div>
            <p className="text-ub-2 mt-2 text-sm">
              {program.price > 0 ? `Secure checkout via Stripe. ${formatPrice(program.price, program.currency)}.` : "Free to register — we'll follow up personally."}
            </p>
            <div className="space-y-3 mt-6">
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" className="w-full px-5 py-3.5 rounded-2xl bg-ub border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" className="w-full px-5 py-3.5 rounded-2xl bg-ub border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
              <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="register-phone" className="w-full px-5 py-3.5 rounded-2xl bg-ub border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors" />
              <textarea rows={3} placeholder="Anything you'd like Anant to know?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="register-message" className="w-full px-5 py-3.5 rounded-2xl bg-ub border border-ub focus:border-[var(--ub-primary)] outline-none transition-colors resize-none" />
            </div>
            <button disabled={busy} data-testid="register-submit-btn" className="w-full mt-4 bg-primary-ub text-white rounded-full py-4 font-medium hover:bg-[var(--ub-primary-hover)] transition-colors disabled:opacity-50">
              {busy ? "Please wait…" : program.price > 0 ? `Pay ${formatPrice(program.price, program.currency)} & Register` : "Register (Free)"}
            </button>
            <p className="text-xs text-muted-ub mt-3">By registering you agree to receive program updates.</p>
            {program.whatsapp && (
              <a
                href={`https://wa.me/${program.whatsapp}?text=${encodeURIComponent("Hi Anant, I'd like to know more about " + program.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="register-whatsapp-btn"
                className="block mt-4 text-center rounded-full py-3 border border-[#25D366] text-[#128C7E] hover:bg-[#25D366] hover:text-white transition-colors font-medium"
              >
                Or WhatsApp to enroll
              </a>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
